import {
  Alert,
  Box,
  Card,
  CardActionArea,
  CardContent,
  CircularProgress,
  Grid,
  MenuItem,
  Typography,
} from "@mui/material"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import TextField from "@mui/material/TextField"
import { useFormik } from "formik"
import { useAppDispatch, useAppSelector } from "hooks"
import { useState } from "react"
import licensePoolService from "services/licensePoolService"
import userService from "services/userService"
import { updateLicense } from "slices/licenseSlice"
import { addUser } from "slices/usersSlice"
import type { IAssignLicense } from "types/licenseTypes"
import type { IOrganization } from "types/organizationTypes"
import { UserRoles } from "types/userRoles"
import type { ICreateUser } from "types/userTypes"
import * as Yup from "yup"

type Props = {
  organization: IOrganization
}

export function CreateUserDialog({ organization }: Props) {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | undefined>(undefined)
  const [open, setOpen] = useState(false)
  const [currentStep, setCurrentStep] = useState(0)
  const [isDashboardUser, setIsDashboardUser] = useState<boolean>()
  const [selectedLicensePoolId, setSelectedLicensePoolId] = useState<string>()

  const userGroups = useAppSelector((state) => state.usergroup.userGroups)

  const licensePools = useAppSelector((state) => state.licensepool.licensePools)

  const dispatch = useAppDispatch()

  const initialValues: ICreateUser = {
    organizationId: organization.id,
    userGroupId: "",
    role: "User",
    pin: "",
    firstname: "",
    lastname: "",
    phonenumber: "",
    email: "",
    locale: "",
  }

  const createDashboardUserValidationScheme = Yup.object({
    userGroupId: Yup.string().required("Please select user group"),
    role: Yup.string().required("Please select user role"),
    pin: Yup.string()
      .required("Please enter user PIN")
      .min(3, "Minimum pin length is 3")
      .max(10, "Maximum pin length is 10")
      .matches(/^[0-9]*$/, "PIN can only contain digits."),
    firstname: Yup.string()
      .required("Please enter user first name")
      .max(64, "Maximum length is 64 characters"),
    lastname: Yup.string()
      .required("Please enter user last name")
      .max(64, "Maximum length is 64 characters"),
    email: Yup.string()
      .required("Please enter user email")
      .email()
      .max(128, "Maximum email length is 128 characters"),
    phonenumber: Yup.string()
      .min(5, "Minimum phone number length is 5")
      .matches(
        /^[+][1234567890]+$/,
        "Phone number is not valid. A phone number must start with a plus (+) sign, followed immediately by the country code. A phone number can only contain the + sign and digits.",
      ),
  })

  const createSimulatorUserValidationScheme = Yup.object({
    userGroupId: Yup.string().required("Please select user group"),
    pin: Yup.string()
      .required("Please enter user PIN")
      .min(3, "Minimum pin length is 3")
      .max(10, "Maximum pin length is 10")
      .matches(/^[0-9]*$/, "PIN can only contain digits."),
    firstname: Yup.string()
      .required("Please enter user first name")
      .max(64, "Maximum length is 64 characters"),
    lastname: Yup.string()
      .required("Please enter user last name")
      .max(64, "Maximum length is 64 characters"),
    phonenumber: Yup.string()
      .min(5)
      .matches(
        /^[+][1234567890]+$/,
        "Phone number is not valid. A phone number must start with a plus (+) sign, followed immediately by the country code. A phone number can only contain the + sign and digits.",
      ),
  })

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: isDashboardUser
      ? createDashboardUserValidationScheme
      : createSimulatorUserValidationScheme,
    onSubmit: (values) => {
      setIsLoading(true)

      const createUser: ICreateUser = {
        organizationId: organization.id,
        userGroupId: values.userGroupId,
        role: isDashboardUser ? values.role : "User",
        pin: values.pin,
        firstname: values.firstname,
        lastname: values.lastname,
        phonenumber: values.phonenumber,
        email: values.email,
        locale: values.locale,
      }

      userService
        .createUser(createUser)
        .then((resp) => {
          dispatch(addUser(resp))

          if (selectedLicensePoolId !== undefined && selectedLicensePoolId !== "") {
            const assignLicense: IAssignLicense = {
              assignedUserId: resp.id,
            }
            licensePoolService
              .assignLicense(selectedLicensePoolId, assignLicense)
              .then((license) => {
                dispatch(updateLicense(license))
                setIsLoading(false)
                handleClose()
              })
              .catch((err) => {
                console.log(err)
                setIsLoading(false)
              })
          } else {
            setIsLoading(false)
            handleClose()
          }
        })
        .catch((err) => {
          console.log(err)
          setIsLoading(false)
          setError("error")
        })
    },
  })

  const handleClickOpen = () => {
    formik.resetForm()
    setCurrentStep(0)
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  const handleClickUserType = (isDashboardUser: boolean) => {
    setCurrentStep(1)
    setIsDashboardUser(isDashboardUser)
  }

  const userTypeSelection = (
    <Grid container spacing={1}>
      <Grid item xs={12} sm={6}>
        <Card>
          <CardActionArea sx={{ minHeight: 135 }} onClick={() => handleClickUserType(true)}>
            <CardContent>
              <Typography gutterBottom variant="h6" component="div">
                Dashboard + Simulator
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This user type is able to use the simulator and the dashboard. This user type
                requires an email.
              </Typography>
            </CardContent>
          </CardActionArea>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Card>
          <CardActionArea sx={{ minHeight: 135 }} onClick={() => handleClickUserType(false)}>
            <CardContent>
              <Typography data-testid="simulatorUser" gutterBottom variant="h6" component="div">
                Simulator
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This user type is only able to use the simulator.
              </Typography>
            </CardContent>
          </CardActionArea>
        </Card>
      </Grid>
    </Grid>
  )

  const createDashboardUser = (
    <Grid container spacing={1}>
      <Grid item xs={12} sm={6}>
        <TextField
          required
          autoFocus
          margin="dense"
          id="firstname"
          label="First name"
          type="firstname"
          fullWidth
          variant="outlined"
          value={formik.values.firstname}
          onChange={formik.handleChange}
          error={formik.touched.firstname && Boolean(formik.errors.firstname)}
          helperText={formik.touched.firstname && formik.errors.firstname}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          required
          margin="dense"
          id="lastname"
          label="Last name"
          type="lastname"
          fullWidth
          variant="outlined"
          value={formik.values.lastname}
          onChange={formik.handleChange}
          error={formik.touched.lastname && Boolean(formik.errors.lastname)}
          helperText={formik.touched.lastname && formik.errors.lastname}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          required
          fullWidth
          margin="dense"
          id="userGroupId"
          label="User Group"
          type="userGroupId"
          name="userGroupId"
          select
          variant="outlined"
          value={formik.values.userGroupId}
          onChange={formik.handleChange}
          error={formik.touched.userGroupId && Boolean(formik.errors.userGroupId)}
          helperText={formik.touched.userGroupId && formik.errors.userGroupId}
        >
          {userGroups.map((userGroup) => (
            <MenuItem key={userGroup.id} value={userGroup.id}>
              {userGroup.name}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={12}>
        <TextField
          required
          fullWidth
          margin="dense"
          id="role"
          label="Role"
          type="role"
          name="role"
          select
          variant="outlined"
          value={formik.values.role}
          onChange={formik.handleChange}
          error={formik.touched.role && Boolean(formik.errors.role)}
          helperText={formik.touched.role && formik.errors.role}
        >
          <MenuItem key={1} value={UserRoles.USER.toString()}>
            User
          </MenuItem>
          <MenuItem key={2} value={UserRoles.ORGANIZATION_ADMIN.toString()}>
            Admin
          </MenuItem>
        </TextField>
      </Grid>
      <Grid item xs={12}>
        <TextField
          required
          margin="dense"
          id="pin"
          label="PIN"
          type="pin"
          fullWidth
          variant="outlined"
          value={formik.values.pin}
          onChange={formik.handleChange}
          error={formik.touched.pin && Boolean(formik.errors.pin)}
          helperText={formik.touched.pin && formik.errors.pin}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          required
          margin="dense"
          id="email"
          label="Email"
          type="email"
          fullWidth
          variant="outlined"
          value={formik.values.email}
          onChange={formik.handleChange}
          error={formik.touched.email && Boolean(formik.errors.email)}
          helperText={formik.touched.email && formik.errors.email}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          margin="dense"
          id="phonenumber"
          label="Phone"
          type="phonenumber"
          fullWidth
          variant="outlined"
          value={formik.values.phonenumber}
          onChange={formik.handleChange}
          error={formik.touched.phonenumber && Boolean(formik.errors.phonenumber)}
          helperText={formik.touched.phonenumber && formik.errors.phonenumber}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          margin="dense"
          id="licensePoolId"
          label="License Pool"
          type="licensePoolId"
          name="licensePoolId"
          select
          variant="outlined"
          value={selectedLicensePoolId}
          onChange={(e) => setSelectedLicensePoolId(e.target.value)}
        >
          <MenuItem key="0" value="">
            No License
          </MenuItem>
          {licensePools.map(
            (licensePool) =>
              licensePool.freeLicenses > 1 && (
                <MenuItem key={licensePool.id} value={licensePool.id}>
                  {licensePool.name}
                </MenuItem>
              ),
          )}
        </TextField>
      </Grid>
    </Grid>
  )

  const createSimulatorUser = (
    <Grid container spacing={1}>
      <Grid item xs={12} sm={6}>
        <TextField
          required
          autoFocus
          margin="dense"
          id="firstname"
          label="First name"
          type="firstname"
          fullWidth
          variant="outlined"
          value={formik.values.firstname}
          onChange={formik.handleChange}
          error={formik.touched.firstname && Boolean(formik.errors.firstname)}
          helperText={formik.touched.firstname && formik.errors.firstname}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          required
          margin="dense"
          id="lastname"
          label="Last name"
          type="lastname"
          fullWidth
          variant="outlined"
          value={formik.values.lastname}
          onChange={formik.handleChange}
          error={formik.touched.lastname && Boolean(formik.errors.lastname)}
          helperText={formik.touched.lastname && formik.errors.lastname}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          required
          fullWidth
          margin="dense"
          id="userGroupId"
          label="User Group"
          type="userGroupId"
          name="userGroupId"
          select
          variant="outlined"
          value={formik.values.userGroupId}
          onChange={formik.handleChange}
          error={formik.touched.userGroupId && Boolean(formik.errors.userGroupId)}
          helperText={formik.touched.userGroupId && formik.errors.userGroupId}
        >
          {userGroups.map((userGroup) => (
            <MenuItem key={userGroup.id} value={userGroup.id}>
              {userGroup.name}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={12}>
        <TextField
          required
          margin="dense"
          id="pin"
          label="PIN"
          type="pin"
          fullWidth
          variant="outlined"
          value={formik.values.pin}
          onChange={formik.handleChange}
          error={formik.touched.pin && Boolean(formik.errors.pin)}
          helperText={formik.touched.pin && formik.errors.pin}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          margin="dense"
          id="phonenumber"
          label="Phone"
          type="phonenumber"
          fullWidth
          variant="outlined"
          value={formik.values.phonenumber}
          onChange={formik.handleChange}
          error={formik.touched.phonenumber && Boolean(formik.errors.phonenumber)}
          helperText={formik.touched.phonenumber && formik.errors.phonenumber}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          margin="dense"
          id="licensePoolId"
          label="License Pool"
          type="licensePoolId"
          name="licensePoolId"
          select
          variant="outlined"
          value={selectedLicensePoolId}
          onChange={(e) => setSelectedLicensePoolId(e.target.value)}
        >
          <MenuItem key="0" value="">
            No License
          </MenuItem>
          {licensePools.map(
            (licensePool) =>
              licensePool.freeLicenses > 1 && (
                <MenuItem key={licensePool.id} value={licensePool.id}>
                  {licensePool.name}
                </MenuItem>
              ),
          )}
        </TextField>
      </Grid>
    </Grid>
  )

  return (
    <div>
      <Button data-testid="createUserButton" variant="contained" onClick={handleClickOpen}>
        create User
      </Button>
      <Dialog open={open} fullWidth maxWidth="sm">
        <form onSubmit={formik.handleSubmit}>
          <DialogTitle>{currentStep === 0 ? "Select User Type" : "Create User"}</DialogTitle>
          <DialogContent>
            {currentStep === 0 && userTypeSelection}
            {currentStep === 1 && (isDashboardUser ? createDashboardUser : createSimulatorUser)}
            {error !== undefined && (
              <Alert severity="error">
                Unable to create the user, make sure the email and PIN are not already in use
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Box sx={{ m: 1, position: "relative" }}>
              <Button type="submit" disabled={isLoading || currentStep === 0}>
                Create
              </Button>
              {isLoading && (
                <CircularProgress
                  size={24}
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    marginTop: "-12px",
                    marginLeft: "-12px",
                  }}
                />
              )}
            </Box>
          </DialogActions>
        </form>
      </Dialog>
    </div>
  )
}
